import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ToggleSwitch from '../ToggleSwitch';
import { PopoverRow } from '../v2/ToolHeader';
import {
    setDetectCues, setSensitivity, setMaxHotCues, setCueMode, setMinDistanceBars, setQuantizeCuesToGrid,
    setKeepOriginalsUntouched, setWriteFileTags, setDetectLoops, setLoopBars, setMaxLoops, setLoopSensitivity,
    setLoopFocus, setSnapToBeat, setLoopMode, setAutoActiveLoop,
    setTargetRekordbox, setTargetSerato, setTargetTraktor, setTargetVirtualdj,
} from '../../redux/slices/cuePointsSettingsSlice';

// DESIGN V2 — the Cues & Loops settings as ONE-LINE rows in the tool
// header's popover. Same slice, same actions as cuePointsSettings.js (the old
// right-hand panel); the long explanations live in `title` tooltips on the
// ⓘ mark, never in the row.
const LOOP_BAR_CHOICES = [4, 8, 16, 32];

const Info = ({ text }) => <span className="v2-info" title={text} aria-label={text}>ⓘ</span>;

const Stepper = ({ value, min, max, onChange, suffix }) => (
    <span className="v2-stepper">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label="decrease">−</button>
        <span className="v2-num">{value}{suffix ? ` ${suffix}` : ''}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label="increase">+</button>
    </span>
);

const Seg = ({ value, options, onChange }) => (
    <span className="v2-chip-wrap">
        {options.map(([val, label]) => (
            <button key={val} type="button" className={`v2-chip sm${value === val ? ' on' : ''}`} onClick={() => onChange(val)}>{label}</button>
        ))}
    </span>
);

export default function CueSettingsPopoverV2() {
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.cuePointsSettings);
    const {
        detectCues = true, sensitivity, maxHotCues, minDistanceBars, cueMode = 'hot',
        quantizeCuesToGrid = true,
        detectLoops = true, loopBars = [4, 8, 16, 32], maxLoops = 4, loopSensitivity = 0.5,
        loopFocus = 'balanced', snapToBeat = true,
        loopMode = 'spare', autoActiveLoop = false,
        targetRekordbox, targetSerato, targetTraktor, targetVirtualdj, writeFileTags, keepOriginalsUntouched,
    } = settings;
    const isMemoryMode = cueMode === 'memory';
    const safeMaxLoops = (Number.isFinite(maxLoops) && maxLoops >= 1 && maxLoops <= 8) ? maxLoops : 6;
    const safeMaxHot = (Number.isFinite(maxHotCues) && maxHotCues >= 1 && maxHotCues <= 8) ? maxHotCues : 8;
    const sensitivityWord = sensitivity >= 0.9 ? 'Strict' : sensitivity >= 0.78 ? 'Balanced' : 'Generous';
    const loopSensWord = loopSensitivity >= 0.7 ? 'Strict' : loopSensitivity >= 0.4 ? 'Balanced' : 'Generous';
    const toggleLoopBar = (bars) => {
        const set = new Set(loopBars);
        if (set.has(bars)) { if (set.size > 1) set.delete(bars); } else set.add(bars);
        dispatch(setLoopBars(LOOP_BAR_CHOICES.filter((b) => set.has(b))));
    };
    const dim = (on) => (on ? undefined : { opacity: 0.4 });

    return (
        <>
            <div className="v2-popover-title">Cues</div>
            <PopoverRow label={<>Detect cues <Info text="Auto-detect cue points with the AI model. Off = loops only." /></>}>
                <ToggleSwitch id="v2-detectCues" checked={detectCues} onChange={(v) => dispatch(setDetectCues(v))} label="" />
            </PopoverRow>
            <div className="v2-popover-row v2-popover-block" style={dim(detectCues)}>
                <div className="v2-row-head">
                    <span>Sensitivity <Info text="How selective detection is. Higher = fewer cues, only the most confident moments. Lower = more cues, including subtler intros and breakdowns." /></span>
                    <span className="v2-faint v2-num">{Number(sensitivity).toFixed(2)} · {sensitivityWord}</span>
                </div>
                <input type="range" min="0.5" max="0.99" step="0.01" value={sensitivity} className="v2-range" disabled={!detectCues}
                    onChange={(e) => dispatch(setSensitivity(parseFloat(e.target.value)))} aria-label="Sensitivity" />
            </div>
            <div className="v2-popover-row" style={dim(detectCues)}>
                <div>Save as <Info text="Hot cues = trigger pads you tap live. Memory cues = a roadmap you scroll to (pads left empty). Memory cues are Rekordbox-only — Serato, Traktor and VirtualDJ always get hot cues." /></div>
                <Seg value={cueMode} options={[['hot', 'Hot cues'], ['memory', 'Memory']]} onChange={(v) => dispatch(setCueMode(v))} />
            </div>
            <div className="v2-popover-row" style={dim(detectCues && !isMemoryMode)} title={isMemoryMode ? 'Memory cues use no pads' : undefined}>
                <div>Max hot cues <Info text="The strongest cues become coloured hot-cue pads (max 8); any extra are saved as memory cues." /></div>
                <Stepper value={safeMaxHot} min={1} max={8} onChange={(n) => dispatch(setMaxHotCues(n))} />
            </div>
            <div className="v2-popover-row" style={dim(detectCues)}>
                <div>Min distance <Info text="Minimum spacing between cues, so they aren't bunched together." /></div>
                <Stepper value={minDistanceBars || 16} min={1} max={64} onChange={(n) => dispatch(setMinDistanceBars(n))} suffix="bars" />
            </div>
            <PopoverRow label={<>Snap cues to beatgrid <Info text="Nudge detected cues onto the nearest beat (loops anchored to them follow). Never moves a cue more than half a beat." /></>}>
                <ToggleSwitch id="v2-quantizeCuesToGrid" checked={quantizeCuesToGrid} onChange={(v) => dispatch(setQuantizeCuesToGrid(v))} label="" />
            </PopoverRow>

            <div className="v2-popover-title" style={{ marginTop: 12 }}>Loops</div>
            <PopoverRow label={<>Detect loops <Info text="Generate beat-aligned loops alongside cues, in the same pass. Off = cues only." /></>}>
                <ToggleSwitch id="v2-detectLoops" checked={detectLoops} onChange={(v) => dispatch(setDetectLoops(v))} label="" />
            </PopoverRow>
            <div className="v2-popover-row" style={dim(detectLoops)}>
                <div>Loop lengths <Info text="Which loop lengths to consider. The cleanest-tiling length is chosen per spot." /></div>
                <span className="v2-chip-wrap">
                    {LOOP_BAR_CHOICES.map((bars) => (
                        <button key={bars} type="button" className={`v2-chip sm${loopBars.includes(bars) ? ' on' : ''}`} onClick={() => toggleLoopBar(bars)}>{bars}</button>
                    ))}
                </span>
            </div>
            <div className="v2-popover-row" style={dim(detectLoops)}>
                <div>Max loops <Info text="How many loops to keep per track (the highest-quality ones)." /></div>
                <Stepper value={safeMaxLoops} min={1} max={8} onChange={(n) => dispatch(setMaxLoops(n))} />
            </div>
            <div className="v2-popover-row v2-popover-block" style={dim(detectLoops)}>
                <div className="v2-row-head">
                    <span>Loop sensitivity <Info text="Higher = fewer, only the cleanest seamless loops. Lower = more loops, including looser sections." /></span>
                    <span className="v2-faint v2-num">{Number(loopSensitivity).toFixed(2)} · {loopSensWord}</span>
                </div>
                <input type="range" min="0" max="1" step="0.05" value={loopSensitivity} className="v2-range" disabled={!detectLoops}
                    onChange={(e) => dispatch(setLoopSensitivity(parseFloat(e.target.value)))} aria-label="Loop sensitivity" />
            </div>
            <div className="v2-popover-row" style={dim(detectLoops)}>
                <div>Loop focus <Info text="What to prioritise when picking loop spots — steady grooves, the drops, or a mix." /></div>
                <Seg value={loopFocus} options={[['grooves', 'Grooves'], ['balanced', 'Balanced'], ['drops', 'Drops']]} onChange={(v) => dispatch(setLoopFocus(v))} />
            </div>
            <PopoverRow label={<>Snap loops to the beat grid <Info text="Keeps loop edges on bar lines so they repeat seamlessly. Recommended on." /></>}>
                <ToggleSwitch id="v2-snapToBeat" checked={snapToBeat} onChange={(v) => dispatch(setSnapToBeat(v))} label="" />
            </PopoverRow>
            <div className="v2-popover-row">
                <div>Loops on Rekordbox pads <Info text="Rekordbox's 8 pads are one bank shared by cues and loops. Spare pads = cues get pads first, loops take what's left. Memory only = loops always become memory loops (CUE/LOOP CALL on a CDJ). Pads first = loops win the pads. Extras are never lost — they become memory cues/loops." /></div>
                <Seg value={loopMode} options={[['spare', 'Spare'], ['memory', 'Memory'], ['priority', 'Pads first']]} onChange={(v) => dispatch(setLoopMode(v))} />
            </div>
            <PopoverRow label={<>Auto Active Loop <Info text="Arm the last loop as Rekordbox's Active Loop — CDJs engage it automatically when playback reaches it, so the track never runs out under you. One per track; picking a loop manually in the editor overrides this." /></>}>
                <ToggleSwitch id="v2-autoActiveLoop" checked={autoActiveLoop} onChange={(v) => dispatch(setAutoActiveLoop(v))} label="" />
            </PopoverRow>

            <div className="v2-popover-title" style={{ marginTop: 12 }}>Write cues & loops to</div>
            <PopoverRow label="Rekordbox"><ToggleSwitch id="v2-targetRekordbox" checked={!!targetRekordbox} onChange={(v) => dispatch(setTargetRekordbox(v))} label="" /></PopoverRow>
            <PopoverRow label="Serato"><ToggleSwitch id="v2-targetSerato" checked={!!targetSerato} onChange={(v) => dispatch(setTargetSerato(v))} label="" /></PopoverRow>
            <PopoverRow label="Traktor"><ToggleSwitch id="v2-targetTraktor" checked={!!targetTraktor} onChange={(v) => dispatch(setTargetTraktor(v))} label="" /></PopoverRow>
            <PopoverRow label="VirtualDJ"><ToggleSwitch id="v2-targetVirtualdj" checked={!!targetVirtualdj} onChange={(v) => dispatch(setTargetVirtualdj(v))} label="" /></PopoverRow>
            <PopoverRow label={<>Embed cue tags in the files <Info text="Writes Serato-style cue tags into the audio files themselves, so the cues travel with the file." /></>}>
                <ToggleSwitch id="v2-writeFileTags" checked={!!writeFileTags} onChange={(v) => dispatch(setWriteFileTags(v))} label="" />
            </PopoverRow>
            <PopoverRow label={<>Keep original files <Info text="Writes to a [cues] copy next to each original instead of tagging the original." /></>}>
                <ToggleSwitch id="v2-keepOriginalsUntouched" checked={!!keepOriginalsUntouched} onChange={(v) => dispatch(setKeepOriginalsUntouched(v))} label="" />
            </PopoverRow>
        </>
    );
}
